
import { Component, Provide, Prop, Mixins } from "vue-property-decorator";
import { CreateElement } from "vue";
import _ from "@/boot/lodash";

// Mixins
import ProvisioningMixin from "@/mixins/provisioningMixin";
import RequestPromises from "@/mixins/requestPromises";

// Enums/ Data
import { DataModules } from "@/store/modules/data";
import { ProductTypes } from "@/data/enums/products";
import { ConfigurationModes } from "@/data/enums/provisioning";
import { LogoSize, LogoType } from "@/data/enums/logos";

// Types
import { IProvider } from "@/models/providers";
import {
  IProvisionCategoriesProviderData,
  IProvisionCategoriesProviderMethods
} from "@/models/providers/provisioning";
import { IProvisionCategory, IProvisionProvider } from "@/models/provisioning";

@Component
export default class ProvisionCategoriesProvider
  extends Mixins(RequestPromises, ProvisioningMixin)
  implements IProvider {
  // Props

  @Prop({ default: false })
  public handleGuarding!: boolean;

  @Prop({ default: false })
  public handleLoading!: boolean;

  @Prop({ default: false })
  public handleRequestError!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  // Variables

  public isLoading = true;
  public isReloading = false;
  public isProcessing = {};
  public hasRequestError = false;

  // Lifecycle methods

  public async created() {
    if (!this.contentIsAvailable) {
      this.isLoading = false;
      return;
    }
    this.loadData();

    this.$bus.$on(
      "refreshAvailableConfigurationsCount",
      this.getAllProvisionConfigurations
    );
  }

  public beforeDestroy() {
    this.$bus.$off("refreshAvailableConfigurationsCount");
  }

  // Methods

  public async loadData() {
    try {
      await Promise.all([
        this.getCategoriesWithProviders(this.isReloading),
        this.getAllProvisionConfigurations(this.isReloading)
      ]);

      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    this.loadData();
  }

  public getProviderLogoUrl(provider: IProvisionProvider) {
    return _.get(
      _.find(provider.logos || [], logo => {
        if (logo.type === LogoType.ICON && logo.size === LogoSize.ONE_X)
          return true;
        return false;
      }),
      "url",
      null
    );
  }

  public openCreatePackageModal() {
    this.$store.dispatch(`data/${DataModules.CATALOGUE}/openAddProductModal`, {
      props: {
        productTypes: [ProductTypes.SINGLE_PRODUCT],
        extraProps: {
          provision_blueprint_id: this.provisionBlueprintId,
          provision_category_id: this.provisionCategoryId,
          provision_configuration_mode: ConfigurationModes.STATIC
        }
      }
    });
  }

  public async openCreateConfigurationModal(
    providerId: IProvisionProvider["id"] | null = null
  ) {
    this.$store.dispatch(
      `data/${DataModules.PROVISIONING_CONFIGURATIONS}/openManageProviderConfigurationModal`,
      {
        total: this.allCategoriesTotal,
        props: _.merge(
          {
            categoryId: this.provisionCategoryId,
            refreshConfigurationList: true
          },
          _.isNull(providerId) ? {} : { providerId }
        )
      }
    );
  }

  // Computed

  public get showContent() {
    return (
      this.contentIsAvailable &&
      !this.isLoading &&
      !this.isReloading &&
      !this.hasRequestError
    );
  }

  public get canAddConfiguration() {
    return this.$userCan("create_provision_configuration");
  }

  public get canAddPackage() {
    return this.$userCan("create_product");
  }

  public get provisionCategory() {
    return _.find(this.categories, [
      "code",
      this.categoryCode
    ]) as IProvisionCategory;
  }

  public get categoryProviders(): IProvisionProvider[] {
    return _.map(
      _.get(this.provisionCategory, "providers", []),
      domainProvider => {
        domainProvider.logo_url = this.getProviderLogoUrl(domainProvider);
        return domainProvider;
      }
    );
  }

  public get provisionBlueprintId() {
    return _.get(this.provisionCategory, "blueprints[0].id", null);
  }

  public get provisionCategoryId() {
    return _.get(this.provisionCategory, "id", null);
  }

  // Provide

  @Provide("provisionCategoriesProvider")
  public providerData(): IProvisionCategoriesProviderData {
    return {
      canAddConfiguration: this.canAddConfiguration,
      canAddPackage: this.canAddPackage,
      categoryProviders: this.categoryProviders,
      contentIsAvailable: this.contentIsAvailable,
      isLoading: this.isLoading,
      isReloading: this.isReloading,
      hasRequestError: this.hasRequestError,
      provisionBlueprintId: this.provisionBlueprintId,
      provisionCategoryId: this.provisionCategoryId,
      showContent: this.showContent
    };
  }

  @Provide("provisionCategoriesMethods")
  public get providerMethods(): IProvisionCategoriesProviderMethods {
    return {
      openCreatePackageModal: this.openCreatePackageModal,
      openCreateConfigurationModal: this.openCreateConfigurationModal,
      reloadData: this.reloadData
    };
  }

  // Guarding

  public guardFunctionalities = ["list_provision_categories"];

  public get contentIsAvailable() {
    return this.$userCan(...this.guardFunctionalities);
  }

  // Life-cycle Methods

  public render(h: CreateElement) {
    // Handle guarding
    if (this.handleGuarding && !this.contentIsAvailable)
      return h("guard", { props: { ifAdmin: false } });

    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });

    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });

    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $provisionCategoriesData: this.providerData(),
        $provisionCategoriesMethods: this.providerMethods
      })
    );
  }
}
